import type { JiraCustomField, JiraCustomFieldsResponse } from '@/types';
import { useQuery } from '@/utils/api';

import { useAxios } from './useAxios';

export interface UseJiraCustomFieldsOptions {
  accountKey?: string;
  projectKey?: string;
  issueType?: string;
  config?: Record<string, unknown>;
  enabled?: boolean;
}

export interface UseJiraCustomFieldsResult {
  fields: JiraCustomField[];
}

/**
 * Fetches the *required custom fields* on the Jira create screen for the
 * given (projectKey, issueType). The backend filters to required && custom
 * — optional fields and built-in required fields (project, issuetype,
 * summary, description) are intentionally not returned.
 *
 * Disabled until both projectKey and issueType are set, mirroring how
 * useJiraTransitions handles its prerequisites.
 */
export const useJiraCustomFields = ({
  accountKey,
  projectKey,
  issueType,
  config,
  enabled = true,
}: UseJiraCustomFieldsOptions) => {
  const axios = useAxios();

  return useQuery({
    defaultErrorMessage: 'Failed to fetch Jira custom fields',
    queryKey: [
      'jira',
      'custom-fields',
      projectKey,
      issueType,
      accountKey ?? 'new',
    ],
    queryFn: async (): Promise<UseJiraCustomFieldsResult> => {
      const response = await axios.post<JiraCustomFieldsResponse>(
        '/integration/jira/custom-fields',
        {
          provider: 'jira',
          account: accountKey,
          secret: accountKey ? undefined : config,
          projectKey,
          issueType,
        }
      );

      return { fields: response.data?.fields ?? [] };
    },
    enabled:
      enabled && !!projectKey && !!issueType && (!!accountKey || !!config),
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
};
